import { defineComponent, computed, reactive, ref, SetupContext } from '@vue/composition-api';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import SystemConst from '@/shared/system-const';
import vueRouter from '@/router/index';                                           // ルート情報取得
import { UserType, PullDown, CompleteAddUser } from '@/shared/type';              // 型定義をimport
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import HttpStatus from 'http-status-codes';                                       // HTTPステータスコード取得用

// reactiveDataの型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                         // Windowサイズ
    isAddMode: boolean;                                                           // true: 登録処理, false: 編集処理
    user: UserType;                                                               // ユーザー情報
    showPassword: boolean;                                                        // 入力パスワード表示（true:表示, false:非表示）
    showRePassword: boolean;                                                      // 再入力パスワード表示（true:表示, false:非表示）
    organizationIdItems: PullDown[];                                              // 所属組織取得
    fiscalYearItems: PullDown[];                                                  // 作成年度（和暦）取得
    showSuccessDialog: boolean;                                                   // 登録成功ダイアログ表示（true:表示, false:非表示）
    showDialogPassword: boolean;                                                  // 登録後パスワード表示（true:表示, false:非表示）
    showTooltip: boolean;                                                         // ツールチップ表示（true:表示, false:非表示）
    completeAddUser: CompleteAddUser;                                             // 登録したユーザー情報
    completeMessage: string;                                                      // 正常終了メッセージ格納
    displayName: string;                                                          // 登録成功ダイアログに表示する氏名
    displayMail: string;                                                          // 登録成功ダイアログに表示するメールアドレス
    displayPassword: string;                                                      // 登録成功ダイアログに表示するパスワード
    displayEye: string;                                                           // 登録成功ダイアログに表示する目のアイコン
    displayUserId: string;                                                        // 画面表示用のユーザーID（開発ツール等で変更できてしまうためback側に渡す変数と分ける
};

export default defineComponent({
    components: { CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const router = vueRouter;                                                 // ルータ
        const commonRef = ref<any>(null);                                         // 共通メッセージダイアログ
        const form = ref<any>(null);
        const formHeight = SystemConst.FORM_HEIGHT.HEIGHT;                        // フォーム高さ調整用変数
        const initialPulldown = { text: '', value: '' };
        const maxLength = {
            // 各登録項目に設定された最大桁数
            identificationCode: SystemConst.USER_MAX_LENGTH.IDENTIFICATION_CODE,
            lastName: SystemConst.USER_MAX_LENGTH.LAST_NAME,
            firstName: SystemConst.USER_MAX_LENGTH.FIRST_NAME,
            lastNameKana: SystemConst.USER_MAX_LENGTH.LAST_NAME_KANA,
            firstNameKana: SystemConst.USER_MAX_LENGTH.FIRST_NAME_KANA,
            mail: SystemConst.USER_MAX_LENGTH.MAIL,
            mailAddress: SystemConst.USER_MAX_LENGTH.MAIL_ADDRESS,
            password: SystemConst.USER_MAX_LENGTH.PASSWORD,
            rePassword: SystemConst.USER_MAX_LENGTH.RE_PASSWORD,
            postName: SystemConst.USER_MAX_LENGTH.POST_NAME
        };
        const accountDivision = {
            // アカウント区分の選択された値
            notEntered: SystemConst.ACCOUNT_DIVISION.NOT_ENABLED,
            teacher: SystemConst.ACCOUNT_DIVISION.TEACHER,
            student: SystemConst.ACCOUNT_DIVISION.STUDENT,
            general: SystemConst.ACCOUNT_DIVISION.GENERAL,
            representative: SystemConst.ACCOUNT_DIVISION.REPRESENTATIVE,
            admin: SystemConst.ACCOUNT_DIVISION.ADMIN
        };
        const mailSet = {
            // メールアドレス（選択）の値
            autofillAddress: SystemConst.MAIL_SET.AUTOFILL_ADDRESS,
            inputDirectAddress: SystemConst.MAIL_SET.INPUT_DIRECT_ADDRESS
        };
        const passwordSet = {
            // パスワード（選択）の値
            autofillPassword: SystemConst.PASSWORD_SET.AUTOFILL_PASSWORD,
            inputDirectPassword: SystemConst.PASSWORD_SET.INPUT_DIRECT_PASSWORD
        };
        const state = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                             // ウィンドウサイズ
            isAddMode: true,                                        // true: 登録処理 false: 編集処理
            user: {
                identificationCode: '',                             // 識別コード
                organizationId: initialPulldown,                    // 所属組織（text: 組織名称, value: 組織ID）
                organizationName: '',                               // 所属組織名（削除処理で利用）
                accountDivision: accountDivision['notEntered'],     // アカウント区分
                isOrganizationManager: false,                       // 組織管理者権限
                lastName: '',                                       // 姓
                firstName: '',                                      // 名
                fullName: '',                                       // 氏名（削除処理で利用）
                lastNameKana: '',                                   // 姓（かな）
                firstNameKana: '',                                  // 名（かな）
                fullNameKana: '',                                   // 氏名かな（削除処理で利用）
                autoSetMail: mailSet['autofillAddress'],            // メールアドレス自動生成
                mail: '',                                           // アドレス部
                domain: '@',                                        // ドメイン
                mailAddress: '',                                    // メールアドレス（削除処理で利用）
                autoSetPassword: passwordSet['autofillPassword'],   // パスワード自動生成
                password: '',                                       // パスワード
                rePassword: '',                                     // パスワード再入力
                postName: '',                                       // 職
                fiscalYear: initialPulldown,                        // 作成年度（text: 和暦, value: 西暦）
                fiscalYearText: '',                                 // 作成年度（削除処理で利用）
                isEnabled: true,                                    // 状態（true:有効, false:無効）
                enabled: ''                                         // 状態（削除処理で利用）
            },
            showPassword: false,                                    // 入力パスワード表示（true:表示, false:非表示）
            showRePassword: false,                                  // 再入力パスワード表示（true:表示, false:非表示）
            organizationIdItems: [],                                // 所属組織取得
            fiscalYearItems: [],                                    // 作成年度（和暦）取得
            showSuccessDialog: false,                               // 登録成功ダイアログ表示（true:表示, false:非表示）
            showDialogPassword: false,                              // 登録後パスワード表示（true:表示, false:非表示）
            showTooltip: false,                                     // ツールチップ表示（true:表示, false:非表示）
            completeAddUser: {
                name: '',                                           // 氏名
                mail: '',                                           // メールアドレス
                password: '',                                       // パスワード
            },
            completeMessage: '',                                    // 正常終了メッセージ格納
            displayName: '',                                        // 登録成功ダイアログに表示する氏名
            displayMail: '',                                        // 登録成功ダイアログに表示するメールアドレス
            displayPassword: '********',                            // 登録成功ダイアログに表示するパスワード
            displayEye: 'mdi-eye-off',                              // 登録成功ダイアログに表示する目のアイコン
            displayUserId: ''                                       // 画面表示用のユーザーID（開発ツール等で変更できてしまうためback側に渡す変数と分ける）
        });
        let userId = '';                                            // 編集対象のユーザーID
        let account = '';                                           // 編集前アカウント区分
        let affiliateId = '';                                       // 編集前所属組織のID
        let address = '';                                           // 編集前アドレス部
        let domain = '';                                            // 編集前ドメイン

        // フォームの各項目のValidationのルール
        // 識別コード
        const identificationCodeRules = [
            (v: any) =>
                !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['識別コード']),
            (v: any) =>
                /^[0-9a-zA-Z]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['識別コード', '半角英数字']),
            (v: any) => CommonFunction.checkLength(v, maxLength['identificationCode'], '識別コード')
        ];
        // アカウント区分
        const accountDivisionRules = [
            (v: any) => v !== accountDivision['notEntered'] || CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['アカウント区分'])
        ];
        // 姓
        const lastNameRules = [
            (v: any) =>
                !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['姓']),
            (v: any) =>
                /^[^/<>=]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['姓', '「/」「<」「>」「=」以外']),
            (v: any) =>
                /^[\S]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX_NAME, ['姓']),
            (v: any) => CommonFunction.checkLength(v, maxLength['lastName'], '姓')
        ];
        // 名
        const firstNameRules = [
            (v: any) =>
                !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['名']),
            (v: any) =>
                /^[^/<>=]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['名', '「/」「<」「>」「=」以外']),
            (v: any) =>
                /^[\S]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX_NAME, ['名']),
            (v: any) => CommonFunction.checkLength(v, maxLength['firstName'], '名')
        ];
        // 姓（かな）
        function lastNameKanaRules(): string[] {
            const errorMsgs = [];               // エラーメッセージ格納用配列
            const v = state.user.lastNameKana;  // 入力された姓（かな）
            if (v === '' || v === null) {
                // 未入力、またはclearアイコン押下
                errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['姓（かな）']));
            }
            if (!/^[\S]*$/.test(v)) {
                errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REGEX_NAME, ['姓（かな）']));
            }
            if (!/^[ぁ-んー]*$/.test(v)) {
                errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REGEX, ['姓（かな）', 'ひらがな']));
            }
            const resultCheckLength = CommonFunction.checkLength(v, maxLength['lastNameKana'], '姓（かな）');
            if (typeof resultCheckLength === 'string') {
                // エラーメッセージが返ってきた場合
                errorMsgs.push(resultCheckLength);
            }
            return errorMsgs;
        }
        // 名（かな）
        function firstNameKanaRules(): string[] {
            const errorMsgs = [];               // エラーメッセージ格納用配列
            const v = state.user.firstNameKana; // 入力された名（かな）
            if (v === '' || v === null) {
                // 未入力、またはclearアイコン押下
                errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['名（かな）']));
            }
            if (!/^[\S]*$/.test(v)) {
                errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REGEX_NAME, ['名（かな）']));
            }
            if (!/^[ぁ-んー]*$/.test(v)) {
                errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REGEX, ['名（かな）', 'ひらがな']));
            }
            const resultCheckLength = CommonFunction.checkLength(v, maxLength['firstNameKana'], '名（かな）');
            if (typeof resultCheckLength === 'string') {
                // エラーメッセージが返ってきた場合
                errorMsgs.push(resultCheckLength);
            }
            return errorMsgs;
        }
        // 所属組織
        const organizationIdRules = [
            (v: any) =>
                v !== initialPulldown || CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['所属組織'])
        ];
        // メールアドレス
        function mailRules(): string[] {
            const errorMsgs = [];           // エラーメッセージ格納用配列
            const v = state.user.mail;      // 入力されたメールアドレス
            let resultCheckAddressLength: string | boolean;
            let resultCheckMailLength: string | boolean;
            if (state.user.autoSetMail === mailSet['autofillAddress']) {
                // メールアドレス自動生成時のチェック項目
                resultCheckAddressLength = CommonFunction.checkLengthRange(v, SystemConst.USER_MIN_LENGTH.MAIL, maxLength['mail'], 'アドレス部');
                resultCheckMailLength = CommonFunction.checkLength(v + state.user.domain, maxLength['mailAddress'], 'メールアドレス');

                if (!/^[^.]/.test(v) || !/[^.]$/.test(v)) {
                    errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_ADDRESS_START_END_ERR, ['ピリオド（.）']));
                }
            } else {
                // 直接入力時のチェック項目
                if (v === '' || v === null) {
                    // 未入力、またはclearアイコン押下
                    errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['メールアドレス']));
                }
                if (!/^[a-zA-Z0-9._'-]*$/.test(v)) {
                    errorMsgs.push(CommonFunction.replaceArray(
                        MessageConst.E_REGEX,
                        ['メールアドレス', '英字、数字、ピリオド（.）、ハイフン（-）、アンダースコア（_）、アポストロフィ（\'）のみ']
                    ));
                }
                if (!/^[^.]/.test(v) || !/[^.]$/.test(v)) {
                    errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_ADDRESS_START_END_ERR, ['ピリオド（.）']));
                }
                resultCheckAddressLength = CommonFunction.checkLengthRange(v, SystemConst.USER_MIN_LENGTH.MAIL, maxLength['mail'], 'アドレス部');
                resultCheckMailLength = CommonFunction.checkLength(v + state.user.domain, maxLength['mailAddress'], 'メールアドレス');
                if (/\.\./.test(v)) {
                    errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_ADDRESS_PERIOD_ERR, ['ピリオド（.）']));
                }
                if (v.length === 1 && /^[^a-zA-Z0-9]{1}/.test(v)) {
                    errorMsgs.push(CommonFunction.replaceArray(
                        MessageConst.E_REGEX,
                        ['メールアドレスを1文字とする場合', '英字、数字のみ']
                    ));
                }
            }
            if (typeof resultCheckAddressLength === 'string') {
                // エラーメッセージが返ってきた場合
                errorMsgs.push(resultCheckAddressLength);
            }
            if (typeof resultCheckMailLength === 'string') {
                // エラーメッセージが返ってきた場合
                errorMsgs.push(resultCheckMailLength);
            }
            return errorMsgs;
        }
        // パスワード
        const passwordAddRules = [
            (v: any) =>
                !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['パスワード']),
            (v: any) =>
                /^[0-9a-zA-Z!-/:-@¥[-`{-~]*$/.test(v) || MessageConst.E_PASSWORD_REGEX,
            (v: any) => CommonFunction.checkLengthRange(v, SystemConst.USER_MIN_LENGTH.PASSWORD, maxLength['password'], 'パスワード')
        ];
        // パスワード再入力
        function rePasswordAddRules(): string[] {
            const errorMsgs = [];               // エラーメッセージ格納用配列
            const v = state.user.rePassword;    // 入力された再入力パスワード
            if (v === '' || v === null) {
                // 未入力、またはclearアイコン押下
                errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['再度パスワード']));
            }
            if (!/^[0-9a-zA-Z!-/:-@¥[-`{-~]*$/.test(v)) {
                errorMsgs.push(MessageConst.E_PASSWORD_REGEX);
            }
            const resultCheckLength = CommonFunction.checkLengthRange(v, SystemConst.USER_MIN_LENGTH.RE_PASSWORD, maxLength['rePassword'], 'パスワード');
            if (typeof resultCheckLength === 'string') {
                // エラーメッセージが返ってきた場合
                errorMsgs.push(resultCheckLength);
            }
            const resultCheckMatch = checkPassMatch(v);
            if (typeof resultCheckMatch === 'string') {
                // エラーメッセージが返ってきた場合
                errorMsgs.push(resultCheckMatch);
            }

            return errorMsgs;
        }
        // パスワード(編集処理)
        const passwordUpdateRules = [
            (v: any) =>
                /^[0-9a-zA-Z!-/:-@¥[-`{-~]*$/.test(v) || MessageConst.E_PASSWORD_REGEX,
            (v: any) => CommonFunction.checkLengthRange(v, SystemConst.USER_MIN_LENGTH.PASSWORD, maxLength['password'], 'パスワード')
        ];
        // パスワード再入力(編集処理)
        function rePasswordUpdateRules(): string[] {
            const errorMsgs = [];               // エラーメッセージ格納用配列
            const v = state.user.rePassword;    // 入力された再入力パスワード
            if (!/^[0-9a-zA-Z!-/:-@¥[-`{-~]*$/.test(v)) {
                errorMsgs.push(MessageConst.E_PASSWORD_REGEX);
            }
            const resultCheckLength = CommonFunction.checkLengthRange(v, SystemConst.USER_MIN_LENGTH.RE_PASSWORD, maxLength['rePassword'], 'パスワード');
            if (typeof resultCheckLength === 'string') {
                // エラーメッセージが返ってきた場合
                errorMsgs.push(resultCheckLength);
            }
            const resultCheckMatch = checkPassMatch(v);
            if (typeof resultCheckMatch === 'string') {
                // エラーメッセージが返ってきた場合
                errorMsgs.push(resultCheckMatch);
            }

            return errorMsgs;
        }
        // 職
        const postNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['postName'], '職')
        ];
        // 状態
        const isEnabledRules = [
            (v: any) =>
                v !== 0 || CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['状態'])
        ];

        /**
         * パスワードが一致しているかチェック
         *
         * @param rePass 再入力パスワードの値
         * @return boolean 一致ならtrue
         *         string 不一致ならエラーメッセージ
         */
        function checkPassMatch(rePass: string): boolean | string {
            if (state.user.password !== '' && rePass !== state.user.password) {
                // パスワードが入力されていて、一致していない場合
                return MessageConst.E_PASSWORD_NOT_MATCH;
            }

            return true;
        }

        /**
         * ユーザー登録/編集画面初期化処理
         */
        created();
        async function created() {
            // 編集対象の情報格納用変数初期化
            let getUpdateUser: any;

            // 編集対象者のユーザーID取得
            const getUserId = computed(() => ctx.root.$route.params.id);
            if (typeof getUserId.value !== 'undefined') {
                // 編集対象の定数が格納されている場合
                // isAddModeをfalseに変更する
                state.isAddMode = false;
                // ユーザーIDを保存
                userId = getUserId.value;
                state.displayUserId = getUserId.value;
            }

            // 登録処理か編集処理かの判定
            if (state.isAddMode) {
                // isAddModeがtrueの場合
                // titleIcon変更
                ctx.root.$store.commit('setTitleIcon', 'mdi-account-plus');

                // mainTitle変更
                ctx.root.$store.commit('setMainTitle', 'ユーザー登録');
            } else {
                // isAddModeがfalseの場合
                // titleIcon変更
                ctx.root.$store.commit('setTitleIcon', 'mdi-account-check');

                // mainTitle変更
                ctx.root.$store.commit('setMainTitle', 'ユーザー編集');
                // 編集対象の情報を取得するAPIを呼び出す
                getUpdateUser = await ctx.root.$store.dispatch('user/getOperateUser',
                    {
                        'userId': userId
                    }
                );

                // 通信結果判別
                if (getUpdateUser.status !== HttpStatus.OK) {
                    if (getUpdateUser.status === HttpStatus.NOT_FOUND) {
                        // HTTPステータスコードが404の場合
                        // displayErrorMessageTransitionDialogメソッドでエラーダイアログを表示する
                        commonRef.value.displayErrorMessageTransitionDialog(getUpdateUser, '/users/list');
                        return;
                    }
                    // HTTPステータスコードが200, 404以外の場合
                    // errorDisplayTransitionControlメソッドでエラー画面を表示する
                    commonRef.value.errorDisplayTransitionControl(getUpdateUser);
                    return;
                }

                // メールアドレス、パスワードの選択を直接指定に変更
                state.user.autoSetMail = mailSet['inputDirectAddress'];
                state.user.autoSetPassword = passwordSet['inputDirectPassword'];
            }

            // プルダウン情報(組織情報＋年度情報)取得APIを呼び出す
            const pullDownsResponse = await ctx.root.$store.dispatch('user/getPullDowns');

            // 通信結果判別
            if (pullDownsResponse.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                // errorDisplayTransitionControlメソッドでエラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(pullDownsResponse);
                return;
            }

            // 通信結果を設定
            state.organizationIdItems = pullDownsResponse.data.organizations;
            state.fiscalYearItems = pullDownsResponse.data.eras;

            // 編集処理か判定
            if (!state.isAddMode) {
                // 編集処理の場合、編集対象ユーザーの情報を設定
                state.user.identificationCode = getUpdateUser.data.operateUser['identificationCode'];
                state.user.accountDivision = getUpdateUser.data.operateUser['accountDivision'];
                state.user.isOrganizationManager = getUpdateUser.data.operateUser['isOrganizationManager'];
                state.user.lastName = getUpdateUser.data.operateUser['lastName'];
                state.user.firstName = getUpdateUser.data.operateUser['firstName'];
                state.user.lastNameKana = getUpdateUser.data.operateUser['lastNameKana'];
                state.user.firstNameKana = getUpdateUser.data.operateUser['firstNameKana'];
                state.user.postName = getUpdateUser.data.operateUser['postName'];
                state.user.isEnabled = getUpdateUser.data.operateUser['isEnabled'];
                // 所属組織はvalueが一致したorganizationIdItemsから表示
                const orgUpdateIndex = state.organizationIdItems.find((v) => v.value === getUpdateUser.data.operateUser['organizationId']);
                if (typeof orgUpdateIndex !== 'undefined') {
                    // valueが一致したものがある場合
                    state.user.organizationId = orgUpdateIndex;
                }
                // 作成年度はvalueが一致したfiscalYearItemsから表示
                const yearUpdateIndex = state.fiscalYearItems.find((v) => v.value === getUpdateUser.data.operateUser['fiscalYear']);
                if (typeof yearUpdateIndex !== 'undefined') {
                    // valueが一致したものがある場合
                    state.user.fiscalYear = yearUpdateIndex;
                }
                // メールアドレスは分割してから表示
                const splitMail = getUpdateUser.data.operateUser['mail'].split('@');
                state.user.mail = splitMail[0];
                state.user.domain = '@' + splitMail[1];

                // 編集前メールアドレス保存
                account = state.user.accountDivision;
                affiliateId = state.user.organizationId.value;
                address = state.user.mail;
                domain = state.user.domain;
            }
        }

        /**
         * 登録/編集の確認を行い、確認メッセージダイアログを表示する
         */
        function displayDialog() {
            // フロントのバリデーションチェックをする
            if (!form.value.validate()) {
                // バリデーションチェックにエラーがある場合
                // 画面上部にスクロールして、バリデーションのエラーを表示する
                const formScroll = document.getElementById('formScroll');
                if (formScroll !== null) {
                    formScroll.scrollTo(0, 0);
                }
                return;
            }

            if (state.user.domain === '@') {
                // ドメインが取得できていない場合、メッセージを表示する
                commonRef.value.simpleDisplayMessageDialog(CommonFunction.replaceArray(MessageConst.E_CREATE_MAIL, ['メールアドレス']));
                return;
            }

            // 登録処理実行確認ダイアログに表示するメッセージを格納する
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['登録']);
            // 確認メッセージダイアログ表示
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
            // 「はい」をクリックした場合はprocessingData()メソッドが呼び出され、
            // 「いいえ」をクリックした場合はダイアログを閉じ、ユーザー登録/編集画面へ戻る
        }

        /**
         * 登録/編集画面にて、確認メッセージダイアログの「はい」を押下時の処理
         * 登録/編集処理を行い一覧画面に戻る
         */
        async function processingData() {
            let response: any;
            // 登録処理か編集処理かの判定
            if (state.isAddMode) {
                // isAddModeがtrueの場合
                // ユーザー登録APIを呼び出す
                response = await ctx.root.$store.dispatch('user/addUser',
                    {
                        user: state.user
                    }
                );
            } else {
                // isAddModeがfalseの場合
                // ユーザー編集APIを呼び出す
                response = await ctx.root.$store.dispatch('user/updateUser',
                    {
                        'userId': userId,
                        user: state.user
                    }
                );
            }

            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.NOT_FOUND) {
                    // HTTPステータスコードが404の場合
                    // displayErrorMessageTransitionDialogメソッドでエラーダイアログを表示する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/users/list');
                    return;
                }
                // HTTPステータスコードが200, 404以外の場合
                // エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // レスポンスから登録したユーザー情報を格納する
            state.completeAddUser = response.data.completeAddUser;

            // レスポンスから正常終了のメッセージを格納する
            state.completeMessage = response.data.message;

            // ダイアログ表示用変数に氏名とメールアドレスを設定する
            state.displayName = state.completeAddUser.name;
            state.displayMail = state.completeAddUser.mail;

            // HTTPステータスコードが200の場合
            // 登録/編集完了メッセージダイアログ表示
            if (state.completeAddUser.password) {
                // パスワード情報が存在する場合、パスワード情報を表示する
                state.showSuccessDialog = true;
            } else {
                // パスワード情報が存在しない場合、登録完了画面を表示する
                commonRef.value.displayMessageTransitionDialog(response, '/users/list');
            }
        }

        /**
         * 登録完了ダイアログのパスワード表示切替
         */
        function displayPassword() {
            // パスワード表示切替のフラグを切り替える
            state.showDialogPassword = !state.showDialogPassword;

            if (state.showDialogPassword) {
                // パスワード表示切替のフラグがtrueの場合、
                // アイコンを切り替え、パスワードを表示する
                state.displayEye = 'mdi-eye';
                state.displayPassword = state.completeAddUser.password;
            } else {
                // パスワード表示切替のフラグがfalseの場合、
                // アイコンを切り替え、パスワードを非表示にする
                state.displayEye = 'mdi-eye-off';
                state.displayPassword = '********';
            }
        }

        /**
         * パスワードをクリップボードにコピーする
         */
        async function copyPassword() {
            try {
                // パスワードをクリップボードにコピーする
                navigator.clipboard.writeText(state.completeAddUser.password);

                // コピー完了のツールチップを表示する
                state.showTooltip = true;
            } finally {
                // 2秒後にツールチップを閉じる
                await new Promise(resolve => setTimeout(resolve, 2000));
                state.showTooltip = false;
            }
        }

        /**
         * 作成年度のクリアアイコン押下時、初期化
         */
        function initializeFiscalYear() {
            if (state.user.fiscalYear === null) {
                state.user.fiscalYear = initialPulldown;
            }
        }

        /**
         * 所属組織変更時
         */
        function initializeOrganizationId() {
            if (state.user.organizationId === null) {
                // クリアアイコン押下時、所属組織初期化
                state.user.organizationId = initialPulldown;
            }
            // アドレス生成
            changeMail()
        }

        /**
         * パスワード（選択）変更時、パスワード（入力）初期化
         */
        function resetPassword() {
            state.user.password = '';
            state.user.rePassword = '';
        }

        /**
         * メールアドレス（選択）変更時
         */
        function changeAutoMail() {
            if (state.user.autoSetMail === mailSet['inputDirectAddress']) {
                // 直接メールアドレス指定に変更された場合
                state.user.mail = '';
            }
            // アドレス生成
            changeMail();
        }

        /**
         * アカウント区分変更時、組織管理者権限変更
         */
        function changeAccount() {
            if (state.user.accountDivision !== accountDivision['teacher']) {
                // アカウント区分が教職員以外を選択した場合
                state.user.isOrganizationManager = false;
            }
            // アドレス生成
            changeMail();
        }

        /**
         * 姓（かな）または名（かな）変更時
         */
        function changeNameKana() {
            if (state.user.autoSetMail === mailSet['inputDirectAddress']) {
                // メールアドレス直接入力の場合
                return;
            }
            // アドレス生成
            changeMail();
        }

        /**
         * 姓（かな）または名（かな）、アカウント区分、所属組織、メールアドレス変更時
         */
        function changeMail() {
            if (state.user.autoSetMail === mailSet['autofillAddress']) {
                // アドレス自動生成の場合
                // メールアドレス格納用変数宣言
                let mailAddress = '';
                // 氏名（かな）のバリデーションチェック
                if (lastNameKanaRules().length !== 0 || firstNameKanaRules().length !== 0) {
                    // どちらか一方でもバリデーションエラーの場合
                    return;
                }
                // 正常なデータが入力されている場合
                // 氏名（かな）ローマ字変換関数呼び出し
                mailAddress = CommonFunction.kanaEnglishChars(state.user.lastNameKana);                      // 姓変換
                mailAddress = mailAddress + '.' + CommonFunction.kanaEnglishChars(state.user.firstNameKana); // 名変換
                // メールアドレス設定
                state.user.mail = mailAddress;
            } else {
                // メールアドレス直接入力の場合
                if (state.user.mail === '') {
                    // メールアドレスが入力されていない場合
                    return;
                }
            }

            if (state.user.accountDivision === '' || state.user.organizationId.value === '') {
                // アカウント区分、所属組織入力チェックをし、どちらか一方でも入力されていなかった場合
                return;
            }

            // メールアドレスのバリデーションチェック
            if (mailRules().length !== 0) {
                // エラーメッセージが返ってきた場合
                return;
            }

            if (!state.isAddMode && state.user.mail === address) {
                // 編集画面で入力されたアドレス部が編集前のデータと変わらない場合
                switch (state.user.accountDivision) {
                    case accountDivision['teacher']:
                    case accountDivision['admin']:
                        // アカウント区分が編集前のデータと変わらず、教職員またはシステム管理者の場合
                        if (state.user.accountDivision === account) {
                            state.user.domain = domain;
                            return;
                        }
                        break;
                    case accountDivision['student']:
                    case accountDivision['general']:
                    case accountDivision['representative']:
                        // アカウント区分が生徒、一般、組織代表の場合
                        // 所属組織が一致しており、編集前アカウント区分データも生徒、一般、組織代表の場合
                        if (
                            affiliateId === state.user.organizationId.value &&
                            (account === accountDivision['student'] ||
                                account === accountDivision['general'] ||
                                account === accountDivision['representative'])
                        ) {
                            state.user.domain = domain;
                            return;
                        }
                        break;
                    default:
                        return;
                }
            }
            // すべて正常に入力されていた場合
            // メールアドレス変更、ドメイン取得APIの関数呼び出し
            getMailAddress();
        }

        /**
         * メールアドレス変更、ドメイン取得API呼び出し
         */
        async function getMailAddress() {
            // ドメイン取得処理中は、ドメインを初期化する
            state.user.domain = '@';
            // ドメイン取得APIを呼び出す
            const response = await ctx.root.$store.dispatch('user/getMailAddress',
                {
                    // パラメータ
                    englishCharsAddress: state.user.mail,            // アドレス部
                    accountDivision: state.user.accountDivision,     // アカウント区分
                    organizationId: state.user.organizationId.value, // 所属組織の組織ID
                    autoSetMail: state.user.autoSetMail,             // メール（選択）
                    userId: userId
                }
            );

            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // エラーダイアログを表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 正常通信の場合
            // 変更されたメールのアドレス部が返ってくるか判別
            if (response.data.address !== '') {
                // アドレス部が返ってきた場合
                // アドレス部を更新する
                state.user.mail = response.data.address;
            }

            // 取得したドメインを設定する
            state.user.domain = '@' + response.data.domain;
        }

        /**
         * 登録/編集画面にて、キャンセルボタン押下時の処理
         */
        function back() {
            // ユーザー登録/編集画面を閉じ、ユーザー一覧画面に戻る
            router.back();
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state,
            commonRef,
            initialPulldown,
            form,
            formHeight,
            maxLength,
            accountDivision,
            mailSet,
            passwordSet,
            identificationCodeRules,
            accountDivisionRules,
            lastNameRules,
            firstNameRules,
            lastNameKanaRules,
            firstNameKanaRules,
            organizationIdRules,
            mailRules,
            passwordAddRules,
            rePasswordAddRules,
            passwordUpdateRules,
            rePasswordUpdateRules,
            postNameRules,
            isEnabledRules,
            initializeFiscalYear,
            initializeOrganizationId,
            displayDialog,
            processingData,
            resetPassword,
            changeAutoMail,
            changeAccount,
            changeNameKana,
            changeMail,
            back,
            displayPassword,
            copyPassword,
            onResize
        };
    }
});
